"use strict";
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ THE FEATURE FLAG REGISTRY. The key set lives here, in code.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * BRS §3.11.3 asks for *"a flag registry with description, default, per-tenant override table"*, and
 * §20.3.7 models the first three of those as a **table**. They are a module instead, and 0034's
 * header carries the argument: the predecessor built that table, made `tenant_feature_flags.flag_key`
 * a foreign key to it, populated it with nothing, and left two Phase-0 tables in which the first
 * override anybody stored would have failed on the constraint. Its own words: *"a table unusable from
 * the day it was created, and invisible because no code path reached it."*
 *
 * What the database keeps is the part only it can — the per-tenant override. What a deploy knows is
 * which flags exist, and a deploy is the thing that changes that.
 *
 * ⚠️⚠️ **A FLAG WITHOUT A READER MAY NOT BE ADDED HERE.** `chunks.md` refused this whole subject once
 * for that reason — *"a flag with no consumer in the product is the 'visible control that does
 * nothing' failure"* — and the predecessor's registry stood empty for a phase while a Control Panel
 * screen administered the empty set. The rule that replaces the argument: every entry below names the
 * code that asks about it, and `registry.test.ts` fails on an entry whose `reader` is blank.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.FEATURE_FLAG_KEYS = exports.FEATURE_FLAGS = exports.MCP_FLAG = exports.ACCESS_TOKENS_ENABLED = exports.DEALS_ENABLED = exports.AGENT_FLAG = exports.WORK_FREE_REVENUE_FLAG = exports.LINE_ITEM_PRICING_FLAG = exports.REPORTS_FLAG = void 0;
exports.isFeatureFlagKey = isFeatureFlagKey;
exports.featureDefaults = featureDefaults;
exports.resolveFeatures = resolveFeatures;
exports.featureMap = featureMap;
/**
 * ⚠️ **`reports.view` is the first and only entry, and reports were chosen rather than settled on.**
 * The predecessor picked the same feature and wrote down why: reports are *"self-contained (its own
 * nav item, its own route tree, its own endpoints), and a plausible plan differentiator — which is
 * exactly the shape Phase 2 §2.4's line-item types will take. Turning it off removes a capability
 * rather than degrading one, so 'it genuinely goes away' is observable rather than a matter of
 * opinion."*
 *
 * ⚠️ Exports were the other candidate and are the counter-example: T-10 routes every export in the
 * product through one shared path, so turning them off would degrade ten screens instead of removing
 * one thing — a gate whose effect nobody could describe.
 *
 * ⚠️ **The BRS's actual plan split cannot be built yet** (§3.8: basic = task + time + project;
 * higher = contract, line item, finance). Those features are Phase 2's. Until they exist, a flag per
 * tier would be a commercial decision expressed as a column.
 */
exports.REPORTS_FLAG = 'reports.view';
/**
 * ⚠️⚠️ **`CONTRACTS_FLAG` (`contracts.view`) WAS HERE AND IS GONE — P2-32, 5 Oct 2026.** Contracts
 * were the thing a higher plan bought until every task came to need a line item: on a plan without
 * them nobody could have created a task at all. The product owners put contracts and line items on
 * every plan (D1); the money layer keeps its own flags below. Migration 0164 deletes the overrides
 * that named the key, as 0097 did for its predecessor, so a resurrected key inherits nothing.
 */
/**
 * C25's, and it is a tier rather than a switch.
 *
 * ⚠️⚠️ **`LINE_ITEM_REQUIRED_FLAG` WAS HERE AND IS GONE — the product owner's ruling of
 * 8 Sep 2026, replacing P2-4.** It made a line item mandatory on every new task, which meant a
 * workspace that had not yet modelled its contracts could not create a task at all. Every workspace
 * is in that state on its first day, and a client stays in it for as long as their commercials take
 * to agree; a gate whose commonest reading is *you cannot use the product yet* was the wrong shape
 * for what it protected.
 *
 * ⚠️ **The concern moved rather than being dropped.** Work attached to no line item is work nobody
 * can bill, and Phase 5 surfaces it as a warning on the PROJECT — a count that can be read, sorted
 * and cleared — rather than as a refusal at the moment somebody is writing a task down.
 *
 * ⚠️ **Removed rather than left unread, because this file's first rule forbids the alternative.** A
 * flag whose reader is gone is the *"visible control that does nothing"* failure in the header
 * above, and `registry.test.ts` fails on an entry with a blank reader. Phase 5 may introduce one
 * again, with a real consumer, when there is something for it to gate.
 */
/**
 * ⚠️ **NOT MONEY**, which is why it survives P2-1's deferral: it exposes the line item's
 * `fixed / time_and_material / non_billable` TYPE, one enum and no amounts. It is the seam the
 * financial layer will hang from when it arrives.
 */
exports.LINE_ITEM_PRICING_FLAG = 'contracts.line_item_pricing';
/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C55 · ONE FLAG FOR TWO CHUNKS, AND THE MOCKUP SAYS SO ON THE SCREEN.    │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * Its settings row reads: *"A licenc és díj sortétel-típus, valamint a projekt nélküli,
 * ügyfélhez tartozó szerződés."* So the licence type (C55) and the client-scoped contract (C56)
 * are one capability to whoever buys it, not two — **`PHASE-5.md` §2.5 mentions no flag at all
 * for the contracts half**, and that gap was found by reading the screenshot rather than the
 * inventory.
 *
 * ⚠️⚠️ **AND THE OFF BEHAVIOUR IS STATED, WHICH IS UNUSUAL AND LOAD-BEARING:** *"Kikapcsolás
 * után a meglévők megmaradnak, de újat nem tudsz felvenni."* Turning it off hides nothing and
 * deletes nothing — existing licence line items and project-less contracts keep working, keep
 * earning and keep appearing. It gates CREATION only. A flag that blanked them would make
 * switching plans destroy revenue somebody already agreed.
 */
exports.WORK_FREE_REVENUE_FLAG = 'contracts.work_free_revenue';
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C27 · THE THIRD REASON A FLAG DEFAULTS **OFF**, AND ITS OWN.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * C24's rule above covers two cases: something that already shipped defaults ON, something a plan
 * sells defaults OFF. This is neither — it is off because **every message spends money with a third
 * party.** A tenant who has never been asked should not be spending, and a default of `true` would
 * mean an upgrade switching it on for everybody at once.
 *
 * ⚠️ **Off is also the honest default for a second reason** — `docs/plan/agent.md` §14 records it as
 * an open question: task titles and worklog notes leave the deployment when the agent runs. Whether
 * that is permitted is a contractual answer per tenant, not an engineering one, and a flag that
 * starts off is the shape that lets the answer be *"not yet"*.
 *
 * ⚠️ **It gates the ENTRANCE, not the address** (`routes.tsx`), and the endpoints refuse with
 * `feature_not_enabled` — rule 6, the server check is the only security. With the flag off `/agent`
 * still renders its own screen, because a route that stops existing falls through to the catch-all,
 * which is one of the four failures CLAUDE.md §4 names.
 */
exports.AGENT_FLAG = 'agent.chat';
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 21 Oct 2026 · DEAL MANAGEMENT IS OFF UNTIL IT IS FINISHED.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner: *"disable it from the code (don't remove) … deals, deal settings all need to
 * be hidden until the feature is finished."* A CODE switch rather than a tenant flag, so nobody can
 * turn an unfinished feature on from the control panel. Off: no *Dealek* route or sidebar section,
 * no *Deal menedzsment* settings, no tentative bookings on the resource planner, and no deal tool
 * offered to the Agent. The API, the schema and the data stay — flipping this back is the whole
 * change. ⚠️ A client's contacts are NOT behind it: they stay (the product owner, the same day).
 */
exports.DEALS_ENABLED = false;
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 27 Oct 2026 · PERSONAL ACCESS TOKENS ARE OFF UNTIL THEY DO SOMETHING.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner: the token card *"currently does nothing … we need this hidden until it's
 * finalized"*, for everybody, existing tokens included. A CODE switch, like `DEALS_ENABLED`: off,
 * the Account screen draws no *Személyes hozzáférési kulcsok* card and the settings search offers
 * none, and the API mints no new token. The schema, the endpoints' code and any rows already
 * written stay — flipping this back is the whole change.
 */
exports.ACCESS_TOKENS_ENABLED = false;
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C51.2 · THE FOURTH FLAG DEFAULTING TO FALSE, AND THE FIRST WHOSE     │
 * │ REASON IS A DOOR RATHER THAN A PRICE. `docs/plan/mcp.md` §13.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `LINE_ITEM_PRICING_FLAG` is off because the capability is commercial —
 * somebody's plan decides. `AGENT_FLAG` is off because it spends money with a third party. This one
 * is off because it opens the product **from outside**: an MCP client is somebody else's software,
 * running on somebody's laptop, holding a credential. A door that arrives open for every tenant on
 * a deploy is a door nobody decided to have.
 */
exports.MCP_FLAG = 'mcp.access';
exports.FEATURE_FLAGS = [
    {
        key: exports.REPORTS_FLAG,
        description: 'Reporting: the report builder, saved reports, and the sidebar section.',
        defaultValue: true,
        reader: 'ReportService (every endpoint) and SidebarService, which omits the section entirely.',
    },
    {
        key: exports.LINE_ITEM_PRICING_FLAG,
        description: 'The line item type selector — fixed, time and material, non-billable. A type, not an amount.',
        defaultValue: false,
        reader: 'ContractService, which refuses a type change without it; the contract and line item details, which hide the column and the control.',
    },
    {
        key: exports.WORK_FREE_REVENUE_FLAG,
        description: 'Munka nélküli bevétel: the licence line-item type, and contracts that hang off a client rather than a project.',
        defaultValue: false,
        reader: 'ContractService, which refuses a NEW licence line item or client-scoped contract without it — existing ones are untouched; the line item form, which hides the type.',
    },
    {
        key: exports.AGENT_FLAG,
        description: 'The Agent: the chat surface, its conversations, and the tools it reads the workspace with.',
        /** ⚠️ See the note above `AGENT_FLAG` — off is a decision, not a placeholder. */
        defaultValue: false,
        reader: 'AgentController (every endpoint, before the model is reached) and routes.tsx, which drops the sidebar row.',
    },
    {
        key: exports.MCP_FLAG,
        description: 'The MCP server: POST /mcp, so the tools the Agent uses are callable from any MCP client.',
        /** ⚠️ See the note above `MCP_FLAG` — off is a decision about a door, not a placeholder. */
        defaultValue: false,
        reader: 'McpController, before any method is dispatched — so a workspace without it gets the same refusal for every request rather than a partial surface.',
    },
];
/** The keys, for validating what an operator sends and what a plan carries. */
exports.FEATURE_FLAG_KEYS = exports.FEATURE_FLAGS.map((flag) => flag.key);
function isFeatureFlagKey(key) {
    return exports.FEATURE_FLAG_KEYS.includes(key);
}
/**
 * Every flag at its default.
 *
 * ⚠️ **The starting point of the resolution, so an unregistered key cannot leak into the answer.**
 * `resolveFeatures` walks the registry rather than the rows it read — a stale row for a flag no
 * deploy registers any more is then simply absent from the result, instead of appearing as a feature
 * nothing can turn off.
 */
function featureDefaults() {
    const defaults = {};
    for (const flag of exports.FEATURE_FLAGS)
        defaults[flag.key] = flag.defaultValue;
    return defaults;
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ THE PRECEDENCE, IN THE PACKAGE BOTH PRINCIPALS IMPORT.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * **tenant override → plan mapping → registry default.**
 *
 * ⚠️⚠️ **HERE RATHER THAN IN THE SERVICE, FOR `mergeRoles`' REASON, WHICH IS ALREADY THIS PROJECT'S
 * WORST CASE FOR A DUPLICATED PREDICATE.** `check.service.ts`: *"the copy that drifts is the one
 * deciding what a UI offers while the server quietly disagrees."* Two principals need this answer —
 * `FeatureService` on the tenant connection, and the Control Panel on the operator connection, which
 * cannot share a `ScopedDb` with it. Written twice, they would eventually differ about an
 * unregistered key or a non-boolean value, and the console would then show a state the product does
 * not honour. Each caller reads its own rows; this decides what they mean.
 *
 * ⚠️ **Values are VALIDATED, not cast** (`hard-won.md` C19-1). The `CHECK` on `plans.feature_flags`
 * only guarantees "an object" — a constraint cannot walk a jsonb object without a subquery — so a
 * `"true"` string in a plan's mapping must not become an entitlement.
 */
function resolveFeatures(overrides, planFlags) {
    return exports.FEATURE_FLAGS.map((flag) => {
        const fromPlan = asBoolean(planFlags[flag.key]);
        const withoutOverride = fromPlan ?? flag.defaultValue;
        const override = asBoolean(overrides[flag.key]);
        if (override !== undefined) {
            return {
                key: flag.key,
                description: flag.description,
                enabled: override,
                source: 'override',
                withoutOverride,
            };
        }
        return {
            key: flag.key,
            description: flag.description,
            enabled: withoutOverride,
            source: fromPlan === undefined ? 'default' : 'plan',
            withoutOverride,
        };
    });
}
/** The effective values alone, for a caller that only needs to ask "may I". */
function featureMap(states) {
    const map = {};
    for (const state of states)
        map[state.key] = state.enabled;
    return map;
}
function asBoolean(value) {
    return typeof value === 'boolean' ? value : undefined;
}
//# sourceMappingURL=registry.js.map